import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from './api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage
  const [token, setTokenState] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wrapper to always sync token with localStorage
  const setToken = (newToken) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
    setTokenState(newToken);
  };

  // Load user profile
  const loadUser = async () => {
    setLoading(true);
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      if (error.response?.status === 401) {
        logout('token_invalid_401');
      }
      throw error;
    }
  };

  // Initialize on mount - FIXED to prevent infinite loops
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    
    if (storedToken) {
      setTokenState(storedToken);
      loadUser().catch((error) => {
        // Logout will be triggered if 401
        // Log other errors in development
        if (process.env.NODE_ENV === 'development' && error.response?.status !== 401) {
          // Development-only error tracking
        }
      });
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { access_token } = response.data;
    
    setToken(access_token);
    
    const check = localStorage.getItem('token');
    if (!check) {
      throw new Error('Failed to save token');
    }
    
    // Load full user profile to get subscription data
    const fullUser = await loadUser();
    return fullUser;
  };

  const register = async (email, password) => {
    const response = await authAPI.register(email, password);
    const { access_token } = response.data;
    
    setToken(access_token);
    
    const check = localStorage.getItem('token');
    if (!check) {
      throw new Error('Failed to save token');
    }
    
    // Load full user profile to get subscription data
    const fullUser = await loadUser();
    return fullUser;
  };

  const logout = async (reason = 'manual') => {
    try {
      // Call backend logout to clear session cookie and delete from database
      await authAPI.logout();
    } catch (error) {
      // Continue with frontend logout even if backend fails
    }
    
    // Clear frontend state
    setToken(null);
    setUser(null);
    
    // Wait for cookie deletion to complete before allowing redirect/re-login
    await new Promise(resolve => setTimeout(resolve, 300));
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    logout,
    refreshUser: loadUser,
    setToken,  // CRITICAL: Export setToken for AuthCallback to update React state
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
